// MeetScribe Meeting Protocol Store Module
import axiosConfig from '@/lib/axiosConfig';

const state = {
  protocol: null,
  tasks: [],
  participants: [],
  segments: [],  // Time-based meeting segments
  mindmap: null, // Mindmap data for visualization
  currentSegmentIndex: null,
  segmentsExpanded: false,
  materialToken: null, // Secure token for API calls
  filters: {
    selectedParticipant: '',
    selectedStatus: 'all'
  },
  loading: false,
  segmentsLoading: false,
  error: null
};

const getters = {
  getProtocol: (state) => state.protocol,
  getTasks: (state) => state.tasks,
  getParticipants: (state) => state.participants,
  getSegments: (state) => state.segments,
  getMindmap: (state) => state.mindmap,
  getCurrentSegmentIndex: (state) => state.currentSegmentIndex,
  areSegmentsExpanded: (state) => state.segmentsExpanded,
  getMaterialToken: (state) => state.materialToken,
  getFilters: (state) => state.filters,
  isLoading: (state) => state.loading,
  isSegmentsLoading: (state) => state.segmentsLoading
};

const mutations = {
  SET_PROTOCOL(state, protocol) {
    state.protocol = protocol;
  },
  SET_MATERIAL_TOKEN(state, token) {
    state.materialToken = token;
  },
  SET_TASKS(state, tasks) {
    state.tasks = tasks;
  },
  SET_PARTICIPANTS(state, participants) {
    state.participants = participants;
  },
  SET_MINDMAP(state, mindmap) {
    state.mindmap = mindmap;
  },
  UPDATE_TASK(state, updatedTask) {
    const index = state.tasks.findIndex(t => t.id === updatedTask.id);
    if (index !== -1) {
      state.tasks.splice(index, 1, updatedTask);
    }
  },
  ADD_PARTICIPANT(state, participant) {
    state.participants.push(participant);
  },
  REMOVE_PARTICIPANT(state, name) {
    const index = state.participants.findIndex(p => p.name === name);
    if (index !== -1) {
      state.participants.splice(index, 1);
    }
    // Also clear assignee from tasks
    state.tasks.forEach(task => {
      if (task.assignee_name === name) {
        task.assignee_name = '';
      }
    });
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  SET_FILTER(state, { filterName, value }) {
    state.filters[filterName] = value;
  },
  SET_SEGMENTS(state, segments) {
    state.segments = segments;
  },
  SET_CURRENT_SEGMENT(state, index) {
    state.currentSegmentIndex = index;
  },
  SET_SEGMENTS_EXPANDED(state, expanded) {
    state.segmentsExpanded = expanded;
  },
  SET_SEGMENTS_LOADING(state, loading) {
    state.segmentsLoading = loading;
  }
};

const actions = {
  // hash is automatically added by axiosConfig interceptor
  async fetchProtocol({ commit }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    try {
      const axiosInstance = axiosConfig;

      const response = await axiosInstance.get('', {
        params: {
          action: 'get_meeting_protocol',
          mindmap_type: 'semantic'  // Use GPT-generated semantic mindmap
        }
      });

      if (response.data && response.data.protocol) {
        commit('SET_PROTOCOL', response.data.protocol);

        // Store material token for secure API calls
        if (response.data.materialToken) {
          commit('SET_MATERIAL_TOKEN', response.data.materialToken);
        }

        // Parse and set tasks (API returns 'actionItems')
        // Tasks now include 'token' field from API for secure updates
        if (response.data.actionItems) {
          commit('SET_TASKS', response.data.actionItems);
        }

        // Parse and set participants from separate API field
        if (response.data.participants) {
          commit('SET_PARTICIPANTS', response.data.participants);
        } else if (response.data.protocol.participants) {
          // Fallback to parsed JSON in protocol
          commit('SET_PARTICIPANTS', response.data.protocol.participants);
        }

        // Set mindmap if available
        if (response.data.mindmap) {
          commit('SET_MINDMAP', response.data.mindmap);
        }

        // Set material info to article store (for MeetingHeader expiration display)
        if (response.data.material) {
          commit('article/setArticleItem', response.data.material, { root: true });
        }
      }
    } catch (error) {
      console.error('Error fetching meeting protocol:', error);
      commit('SET_ERROR', error.message);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async updateTask({ commit }, taskData) {
    try {
      const axiosInstance = axiosConfig;

      // Use URLSearchParams to send as form-encoded data (PHP $_POST)
      const params = new URLSearchParams();
      params.append('action', 'update_meeting_task');

      // Prefer secure token over numeric ID
      if (taskData.token) {
        params.append('task_token', taskData.token);
      } else {
        params.append('task_id', taskData.id);
      }

      if (taskData.material_id) {
        params.append('material_id', taskData.material_id);
      }
      if (taskData.assignee_name !== undefined) {
        params.append('assignee_name', taskData.assignee_name || '');
      }
      if (taskData.status) {
        params.append('status', taskData.status);
      }
      if (taskData.notes !== undefined) {
        params.append('notes', taskData.notes || '');
      }
      if (taskData.priority) {
        params.append('priority', taskData.priority);
      }
      if (taskData.task_text !== undefined) {
        params.append('task_text', taskData.task_text);
      }

      const response = await axiosInstance.post('', params, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });

      if (response.data && response.data.success) {
        commit('UPDATE_TASK', taskData);
      }

      return response.data;
    } catch (error) {
      console.error('Error updating task:', error);
      throw error;
    }
  },

  async addParticipant({ commit, state }, { name, role }) {
    try {
      const axiosInstance = axiosConfig;

      // Use secure material_token from state (set when fetchProtocol succeeded)
      const params = new URLSearchParams();
      params.append('action', 'add_meeting_participant');
      params.append('material_token', state.materialToken);
      params.append('name', name);
      params.append('role', role || '');

      const response = await axiosInstance.post('', params, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });

      if (response.data && response.data.success) {
        commit('ADD_PARTICIPANT', { name, role, is_manual: true });
      }

      return response.data;
    } catch (error) {
      console.error('Error adding participant:', error);
      throw error;
    }
  },

  async removeParticipant({ commit, state }, { name }) {
    try {
      const axiosInstance = axiosConfig;

      // Use secure material_token from state
      const params = new URLSearchParams();
      params.append('action', 'remove_meeting_participant');
      params.append('material_token', state.materialToken);
      params.append('name', name);

      const response = await axiosInstance.post('', params, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });

      if (response.data && response.data.success) {
        commit('REMOVE_PARTICIPANT', name);
      }

      return response.data;
    } catch (error) {
      console.error('Error removing participant:', error);
      throw error;
    }
  },

  setFilter({ commit }, { filterName, value }) {
    commit('SET_FILTER', { filterName, value });
  },

  // Segments actions
  // hash is automatically added by axiosConfig interceptor
  async fetchSegments({ commit }) {
    commit('SET_SEGMENTS_LOADING', true);

    try {
      const axiosInstance = axiosConfig;

      const response = await axiosInstance.get('', {
        params: {
          action: 'get_meeting_segments'
        }
      });

      if (response.data && response.data.segments) {
        // Parse JSON fields
        const segments = response.data.segments.map(segment => ({
          ...segment,
          topics: segment.topics_json ? JSON.parse(segment.topics_json) : [],
          speakers: segment.speakers_json ? JSON.parse(segment.speakers_json) : [],
          key_points: segment.key_points_json ? JSON.parse(segment.key_points_json) : []
        }));
        commit('SET_SEGMENTS', segments);
      }
    } catch (error) {
      console.error('Error fetching segments:', error);
    } finally {
      commit('SET_SEGMENTS_LOADING', false);
    }
  },

  setCurrentSegment({ commit }, index) {
    commit('SET_CURRENT_SEGMENT', index);
  },

  toggleAllSegments({ commit, state }) {
    commit('SET_SEGMENTS_EXPANDED', !state.segmentsExpanded);
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
